<template>
  <div class="buy-box-card" :style="cartStyle">
    <EventBuyBoxSaleBanner
      v-if="hasSale"
      class="rounded-t-2xl"
      :has-sale="hasSale"
      :event-data="eventData"
      :is-private="isPrivate"
    />
    <EventBuyBoxHeader
      class="mt-2"
      :loading-pricings="loadingPricings"
      :selected-date-type="
        selectedDateType === 'PUBLIC_PRIVATE'
          ? bookAsTDBToggle
            ? 'PRIVATE'
            : 'PUBLIC'
          : selectedDateType
      "
      :selected-date="selectedDate"
      :selected-ticket-quantity="selectedTicketQuantity"
      :date-request-ticket-quantity="
        userHasChangedTicketQuantity ? selectedTicketQuantity : undefined
      "
      :available-tickets="availableTickets"
      :is-box-event="isBoxEvent"
      :enable-subdomain="enableSubdomain"
      :is-request-buttons-visible="eventBuyBoxVisibleElements.requestButtons"
    />

    <hr class="mt-3 mb-4" />

    <div ref="quantitySelectorRef">
      <LazyEventBuyBoxCategoryQuantitySelector
        v-if="hasTicketCategories && !isGroupPricing"
        :model-value="selectedTicketQuantity"
        :categories="ticketCategoriesArray"
        :selected-date="selectedDate"
        :is-booking-as-private="isPrivate"
        :is-box-event="isBoxEvent"
        :selected-ticket-quantity="selectedTicketQuantity"
        :is-dates-loaded="isDatesLoaded"
        :loading-pricings="loadingPricings"
        :initial-category-quantities="
          hasQueryCategoryQuantities ? selectedCategoryQuantities : undefined
        "
        @update:model-value="onUserTicketQuantityChange"
        @update:categories="handleCategoryUpdate"
      />
      <LazyEventBuyBoxQuantitySelector
        v-else
        :model-value="selectedTicketQuantity"
        :selected-date="selectedDate"
        :is-booking-as-private="isPrivate"
        :is-box-event="isBoxEvent"
        :is-dates-loaded="isDatesLoaded"
        :is-group-pricing="eventGetters.isGroupPricing(eventData)"
        @update:model-value="onUserTicketQuantityChange"
      />
    </div>

    <div class="my-2">
      <LazyEventBuyBoxTieredPricingProgress
        v-if="hasTieredPricing"
        :selected-date="selectedDate"
        :selected-category-quantities="selectedCategoryQuantities"
        :selected-ticket-quantity="selectedTicketQuantity"
        :pricing-data="selectedDatePricingData"
        :is-private="isPrivate"
        :loading-pricings="loadingPricings"
        :event-data="eventData"
      />
    </div>
    <div v-show="eventBuyBoxVisibleElements.calendar">
      <!--
      Following v-if -else ladder covers 4 possible UI versions:
      - 1: Show dates list if available dates quantity < 5
      - 2: Show dropdown select if available dates quantity > 5 and < 10
      - 3: Show date picker and time slots <-- This would be the most common case
      - 4: Show multidate select if nextInstance is a multi-date event
      -->
      <EventBuyBoxDatesList
        v-if="shouldShowDatesList"
        :event-id="eventGetters.getId(eventData)"
        @update-selected-date="(newDate) => (selectedDate = newDate)"
      />
      <EventBuyBoxDatesSelect
        v-else-if="shouldShowDatesSelectDropdown"
        :event-id="eventGetters.getId(eventData)"
        @update-selected-date="(newDate) => (selectedDate = newDate)"
      />
      <div v-else-if="!isMultiDate" class="flex flex-col gap-4 my-4">
        <EventBuyBoxDatePicker
          v-model="selectedDay"
          :first-available-date="firstAvailableDate"
          :first-available-month="firstAvailableMonth"
          :selected-date="selectedDate"
          :selected-tickets-quantity="selectedTicketQuantity"
          :date-request-ticket-quantity="
            userHasChangedTicketQuantity ? selectedTicketQuantity : undefined
          "
          :enable-date-request-calendar-cells="true"
          @open-date-picker="onOpenDatePicker"
          @close-date-picker="onCloseDatePicker"
          @on-dates-loaded="handleDatesLoaded"
        />
        <LazyEventBuyBoxTimeSlots
          v-model="selectedDate"
          :selected-day="selectedDay"
        />
      </div>
      <EventBuyBoxMultidateSelect
        v-else
        :event-id="eventGetters.getId(eventData)"
        @update-selected-date="(newDate) => (selectedDate = newDate)"
      />
      <div
        v-if="isMultiDate && showMultidateSelectionError"
        class="text-red text-xs mt-1"
      >
        {{ t('product.show.checkout.multidateSelectRequiredError') }}
      </div>
    </div>

    <div>
      <InfoBanner
        v-if="
          selectedDate &&
          !isKitsPage &&
          (infoBannerText || selectedDateType === 'PUBLIC_PRIVATE')
        "
        class="my-2"
        :info-text="infoBannerText || ''"
      >
        <div
          v-if="selectedDateType === 'PUBLIC_PRIVATE'"
          class="flex items-center text-xs"
        >
          <Toggle v-model="bookAsTDBToggle" />
          <span class="text-stone-700">
            {{
              t('product.show.infoBanner.privateToggleLabel', {
                privateEventExtraPrice,
              })
            }}
          </span>
        </div>
      </InfoBanner>
      <EventBuyBoxAddonPicker
        v-if="!isBoxEvent"
        v-model="selectedAddOns"
        :is-booking-private="isPrivate"
        :ticket-quantity="selectedTicketQuantity"
        @update:has-add-ons="eventHasAddOns = $event"
      />
    </div>
    <div>
      <Button
        class="w-full mt-2 py-3"
        data-test-id="add-to-cart-button"
        :disabled="shouldDisableMainButton"
        :style="style"
        @click="handleClickMainButton"
      >
        {{ mainButtonLabel }}
      </Button>
    </div>

    <template
      v-if="
        eventBuyBoxVisibleElements.giftcard &&
        enableGiftcard &&
        !eventGetters.getIsTeamEvent(eventData)
      "
    >
      <hr class="mt-4" />

      <EventBuyBoxGiftcard
        :pricings="giftcardPricings"
        :background-color="backgroundColor"
        :text-color="textColor"
        class="mt-4"
        :enable-embedded="isEmbedded"
        @add-item-to-cart="emit('add-item-to-cart', $event)"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { EventBuyBoxDatePicker } from '#components'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import type { CartItem } from '~/composables/types/cart'
import type {
  EventAddOn,
  EventCalendarDay,
  EventCalendarDayMap,
  EventDate,
  EventDescription,
  PricingCategory,
} from '~/composables/types/event'
import { useEventPricings } from '~/composables/useEventPricings'
import { useEventPricingsGetters } from '~/composables/useEventPricingsGetters'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { useTicketQuantityCalculations } from '~/composables/useTicketQuantityCalculations'
import parseEventDateToCartItem from '~/utils/parseEventDateToCartItem'
import {
  mergeAddToCartCategoryQuantities,
  parseAddToCartCategoryQuantitiesFromQuery,
} from '~/utils/parseAddToCartCategoryQuantitiesFromQuery'
import { PREFERRED_DATE_ID_FROM_QUERY_KEY } from '~/composables/usePreferredDateIdFromQuery'

const props = defineProps<{
  enableGiftcard: boolean
  isEmbedded?: boolean
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
  isKitsPage?: boolean
  firstAvailableDate?: string
  firstAvailableMonth?: EventCalendarDayMap
  isMultiDate?: boolean
}>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
  (e: 'dates-loaded' | 'toggle-date-picker' | 'pricings-loaded'): void
  (e: 'update:ticket-quantity', quantity: number): void
}>()

const enableSubdomain = useIsSubdomain()
const eventBuyBoxVisibleElements = {
  calendar: true,
  requestButtons: true,
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    calendar?: boolean
    requestButtons?: boolean
    giftcard?: boolean
  }),
}
const eventData = inject<EventDescription>('eventData')!

// Note: eventData is asserted as non-null because parent component (EventDetails)
// uses v-if="eventData" to prevent rendering this component when data is not available

const { locale, t } = useI18n()
const route = useRoute()
const { pricingsFactory } = usePricingsFactory()
const {
  addItem,
  clear: clearCart,
  isInCart,
  cart,
} = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const { shouldQuickCheckout } = useBookNowExperiment()
const localePath = useLocalePath()
const { sendAddToCartEvent } = useAlgoliaConversion()
const { toggleDateRequestSidebar, setDateRequestSuggestedTicketCount } =
  useUiState()

const isMultiDate = computed(
  () =>
    props.isMultiDate ??
    eventDateGetters.isMultiDate(eventGetters.getNextInstance(eventData))
)
const isBoxEvent = computed(() => eventGetters.isBoxEvent(eventData))
const quantitySelectorRef = ref<HTMLDivElement | undefined>(undefined)
const selectedDay = ref<EventCalendarDay | undefined>(undefined)
const selectedDate = ref<EventDate | undefined>(undefined)
const startedDebouncedFetchPricings = ref(false)
const selectedDateType = computed(
  () => selectedDate.value?.dateType || 'PUBLIC'
)
const isDatesLoaded = ref<boolean>(false)
const bookAsTDBToggle = ref(route.query.date_type?.toString() === 'PRIVATE')
const selectedAddOns = ref<EventAddOn[] | undefined>(undefined)
const eventHasAddOns = ref(false)

const { getInitialCategoryQuantities, totalTickets } =
  useTicketQuantityCalculations()
const ticketCategoriesArray = computed<PricingCategory[]>(
  () => eventData?.pricing?.data?.pricingCategories?.data || []
)

const queryCategoryQuantities = parseAddToCartCategoryQuantitiesFromQuery(
  route.query
)
const hasQueryCategoryQuantities = queryCategoryQuantities !== undefined

const selectedCategoryQuantities = ref<Record<string, number>>(
  hasQueryCategoryQuantities
    ? mergeAddToCartCategoryQuantities(
        ticketCategoriesArray.value,
        queryCategoryQuantities
      )
    : getInitialCategoryQuantities(ticketCategoriesArray.value)
)
const selectedTicketQuantity = ref(
  hasQueryCategoryQuantities
    ? totalTickets(selectedCategoryQuantities.value)
    : 1
)
emit('update:ticket-quantity', selectedTicketQuantity.value)
const userHasChangedTicketQuantity = ref(hasQueryCategoryQuantities)
const availableDatesQuantity = computed(() =>
  eventGetters.getTotalAvailableDates(eventData)
)
const hasTieredPricing = computed(() =>
  eventGetters.hasTieredPricing(eventData)
)
const shouldShowDatesList = computed(
  () =>
    !isMultiDate.value && !props.isKitsPage && availableDatesQuantity.value < 5
)

const shouldShowDatesSelectDropdown = computed(
  () =>
    !isMultiDate.value &&
    !props.isKitsPage &&
    availableDatesQuantity.value >= 5 &&
    availableDatesQuantity.value <= 10
)

const isPrivate = computed(
  () => selectedDateType.value === 'PRIVATE' || bookAsTDBToggle.value
)

const isGroupPricing = computed(() => eventGetters.isGroupPricing(eventData))

const hasTicketCategories = computed(
  () =>
    eventData?.pricing?.data?.pricingCategories?.data &&
    eventData.pricing.data.pricingCategories.data.length > 1
)

const handleCategoryUpdate = (quantities: Record<string, number>) => {
  selectedCategoryQuantities.value = quantities
}
const hasSettedMinQuantityOnce = ref(false)

const selectedDateId = computed(() => selectedDate.value?.id)

const {
  fetchPricings,
  pricings: pricingsData,
  loading: loadingPricings,
} = useEventPricings(ref(eventData) as Ref<EventDescription | undefined>, {
  dateId: selectedDateId,
  quantity: selectedTicketQuantity,
  isPrivate,
  categoryQuantities: selectedCategoryQuantities,
})

const debouncedFetchPricings = useDebounceFn(async () => {
  startedDebouncedFetchPricings.value = true
  await fetchPricings()
  startedDebouncedFetchPricings.value = false
}, 800)

const { pricingData: selectedDatePricingData, hasSale } =
  useEventPricingsGetters(pricingsData, {
    dateId: selectedDateId,
    quantity: selectedTicketQuantity,
    event: ref(eventData) as Ref<EventDescription | undefined>,
  })

provide('pricingsData', pricingsData)
provide('isMultiDateOverride', isMultiDate)

const preferredDateIdFromQuery = computed(() =>
  route.query.add_event_to_cart_date_id?.toString()
)
provide(PREFERRED_DATE_ID_FROM_QUERY_KEY, preferredDateIdFromQuery)

const cardBackgroundColor = computed(() => {
  return props.cardBackgroundColor
})

const cartStyle = computed(() => {
  return {
    ...(cardBackgroundColor.value && {
      backgroundColor: cardBackgroundColor.value,
    }),
  }
})

const style = computed(() => {
  return {
    ...(props.textColor && { color: `#${props.textColor}` }),
    ...(props.backgroundColor && {
      backgroundColor: `#${props.backgroundColor}`,
    }),
  }
})

const infoBannerText = computed(() => {
  if (isMultiDate.value) {
    return t('product.show.infoBanner.multidate')
  }
  return undefined
})

const privateEventExtraPrice = computed(() => {
  const minPrivatePriceAmount = eventDateGetters.getMinimumPrivatePriceAmount(
    selectedDate.value
  )
  const currentTotalDatePrice =
    eventDateGetters.getPriceAmount(selectedDate.value) *
    selectedTicketQuantity.value

  const extraPrice = Math.max(minPrivatePriceAmount - currentTotalDatePrice, 0)
  if (extraPrice === 0) return ''
  const formattedExtraPrice = formatMoney(
    extraPrice / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    2
  )
  return `(+${formattedExtraPrice})`
})

const giftcardPricings = pricingsFactory(
  Number(
    eventData.retail_price?.amount ||
      eventData.original_price?.amount ||
      eventData.default_price.amount
  ),
  {
    totalParticipants: 1,
  }
)

const availableTickets = computed(() =>
  eventDateGetters.getAvailableTicketsQuantity(selectedDate.value)
)

const notEnoughTickets = computed(
  () =>
    selectedDate.value && selectedTicketQuantity.value > availableTickets.value
)

const shouldDisableMainButton = computed(() => {
  // Multidate never fetches pricings until a session is picked, so
  // loadingPricings would otherwise stay stuck at its initial `true` forever.
  if (!selectedDate.value) return !isMultiDate.value

  // Disable if it's a box event and there aren't enough tickets available
  return (
    (isBoxEvent.value && notEnoughTickets.value) ||
    loadingPricings.value ||
    startedDebouncedFetchPricings.value
  )
})

const showMultidateSelectionError = ref(false)

// Client-only so SSR/hydration always render the control ("add to cart") copy:
// the bookNow bucket is client-driven and CloudFront's cache key excludes the
// cookie, so the cached HTML must not depend on the variant.
const isMounted = ref(false)
onMounted(() => {
  isMounted.value = true
})

// Single source of truth for whether this booking goes straight to checkout —
// drives both the CTA copy and the post-add navigation. (KFT-3914)
const willQuickCheckout = computed(
  () =>
    isMounted.value &&
    shouldQuickCheckout({
      isBoxEvent: isBoxEvent.value,
      isKitsPage: props.isKitsPage,
      isEmbedded: props.isEmbedded,
      eventHasAddOns: eventHasAddOns.value,
      hasSelectedAddOn: (selectedAddOns.value?.length ?? 0) > 0,
      cartHasItems: (cart.value?.items?.length ?? 0) > 0,
    })
)

const baseButtonLabel = computed(() => {
  if (isBoxEvent.value && availableTickets.value === 0) {
    return t('product.show.checkout.tryWaitlistButtonText')
  }

  if (notEnoughTickets.value && !isBoxEvent.value) {
    return t('product.show.checkout.tryDateRequestButtonText')
  }

  // Reflect the real action: "Zur Kasse" when going straight to checkout,
  // otherwise the current add-to-cart copy ("In den Warenkorb").
  return willQuickCheckout.value
    ? t('cart.checkoutButton')
    : t('product.show.checkout.book')
})

// While a booking is in flight the label is held steady: adding the item makes
// the cart non-empty, which would otherwise flip the copy to add-to-cart for a
// frame just before we navigate to checkout. (KFT-3914)
const frozenButtonLabel = ref<string | null>(null)
const mainButtonLabel = computed(
  () => frozenButtonLabel.value ?? baseButtonLabel.value
)

const handleClickMainButton = () => {
  if (!selectedDate.value && isMultiDate.value) {
    showMultidateSelectionError.value = true
    return
  }

  const cannotAddToCart = !selectedDate.value || !selectedDatePricingData.value
  if (cannotAddToCart) {
    return
  }

  if (notEnoughTickets.value) {
    const route = useRoute()
    toggleDateRequestSidebar(
      route.name,
      'buybox_cta_insufficient_tickets',
      undefined,
      userHasChangedTicketQuantity.value
        ? selectedTicketQuantity.value
        : undefined
    )
    return
  }
  book()
}

const onOpenDatePicker = () => {
  if (quantitySelectorRef?.value) {
    const offsetTop = quantitySelectorRef.value.offsetTop
    window.scrollTo({
      top: offsetTop - 50,
      behavior: 'smooth',
    })
  }

  emit('toggle-date-picker')
}

const onCloseDatePicker = () => {
  emit('toggle-date-picker')
}

const handleDatesLoaded = async () => {
  isDatesLoaded.value = true
  await nextTick()
  emit('dates-loaded')
}

const book = async () => {
  const queryId = route?.query?.qid?.toString() || ''
  if (!selectedDate.value) {
    return
  }

  const cartItem = parseEventDateToCartItem(
    eventData,
    selectedTicketQuantity,
    selectedDatePricingData.value,
    selectedDate as Ref<EventDate>,
    selectedAddOns,
    isPrivate.value,
    queryId
  )

  if (cartItem) {
    // Hold the CTA copy steady through the add + navigate so it doesn't flip.
    frozenButtonLabel.value = baseButtonLabel.value

    emit('add-item-to-cart', cartItem)

    if (props.isEmbedded) {
      clearCart()
    }

    // Decide BEFORE adding — quick-checkout only applies when the cart was empty
    // (first item of the session); adding this item would otherwise make the
    // cart non-empty and mask the batching rule. (KFT-3914)
    const goToCheckout = willQuickCheckout.value

    await addItem(cartItem)

    // "Book Now" variation: send event bookings straight to checkout instead of
    // opening the cart sidebar (same decision that drives the CTA copy). Falls
    // back to the sidebar if the item didn't actually land in the cart.
    if (goToCheckout && isInCart(cartItem)) {
      await navigateTo(localePath('/checkout'))
      // Navigation unmounts this buy box; the frozen label stays until then.
    } else {
      toggleCartSidebar()
      // No navigation — restore the reactive label to reflect the new cart.
      frozenButtonLabel.value = null
    }
  }

  sendAddToCartEvent(eventData)
}

const onUserTicketQuantityChange = (ticketCount: number) => {
  selectedTicketQuantity.value = ticketCount
  userHasChangedTicketQuantity.value = true
  setDateRequestSuggestedTicketCount(ticketCount)
}

const setMinQuantity = async () => {
  if (hasQueryCategoryQuantities) {
    hasSettedMinQuantityOnce.value = true
    return
  }

  if (!hasSettedMinQuantityOnce.value) {
    let quantityToSet: number

    if (isGroupPricing.value) {
      quantityToSet =
        eventGetters.getGroupPricingFirstScaleMaxParticipants(eventData) ?? 1
    } else if (isPrivate.value) {
      quantityToSet = eventDateGetters.getPrivateMinTickets(selectedDate.value)
    } else {
      return
    }

    selectedTicketQuantity.value = quantityToSet
    userHasChangedTicketQuantity.value = false
    setDateRequestSuggestedTicketCount(undefined)
    await nextTick()
    hasSettedMinQuantityOnce.value = true
  }
}

watch(
  () => selectedTicketQuantity.value,
  (quantity) => {
    startedDebouncedFetchPricings.value = true
    debouncedFetchPricings()
    emit('update:ticket-quantity', quantity)
  }
)

onBeforeUnmount(() => {
  userHasChangedTicketQuantity.value = false
  setDateRequestSuggestedTicketCount(undefined)
})

watch(
  () => selectedDate.value,
  async (newDate) => {
    if (newDate) showMultidateSelectionError.value = false
    await setMinQuantity()
    // needs to fetch pricings from the api if the date is changed
    await fetchPricings()
  }
)

watch(
  () => selectedDay.value,
  () => {
    const dates = selectedDay.value?.dates || []
    const preferredDateId = route.query.add_event_to_cart_date_id?.toString()

    const preferredDate = preferredDateId
      ? dates.find(
          (date) =>
            date.id === preferredDateId &&
            eventDateGetters.getAvailableTicketsQuantity(date) > 0
        )
      : undefined

    const firstAvailableTicket =
      preferredDate ||
      dates.find(
        (date) => eventDateGetters.getAvailableTicketsQuantity(date) > 0
      )

    if (firstAvailableTicket) selectedDate.value = firstAvailableTicket
  },
  { immediate: true, deep: true }
)

watch(
  () => isPrivate.value,
  async () => {
    // Fetch new pricing when private event toggle changes
    if (selectedDate.value) {
      await setMinQuantity()
      await fetchPricings()
    }
  }
)

watch(loadingPricings, async (isLoading, wasLoading) => {
  if (wasLoading && !isLoading) {
    await nextTick()
    emit('pricings-loaded')
  }
})

const hasAttemptedCategoryQueryAddToCart = ref(false)

const canAutoAddFromCategoryQuery = computed(() => {
  if (!hasQueryCategoryQuantities || hasAttemptedCategoryQueryAddToCart.value) {
    return false
  }

  if (!selectedDate.value || !selectedDatePricingData.value) {
    return false
  }

  if (selectedTicketQuantity.value <= 0) {
    return false
  }

  if (loadingPricings.value || startedDebouncedFetchPricings.value) {
    return false
  }

  if (isBoxEvent.value && notEnoughTickets.value) {
    return false
  }

  return true
})

const tryAutoAddFromCategoryQuery = async () => {
  if (!canAutoAddFromCategoryQuery.value) {
    return
  }

  hasAttemptedCategoryQueryAddToCart.value = true

  if (notEnoughTickets.value) {
    toggleDateRequestSidebar(
      route.name,
      'buybox_cta_insufficient_tickets',
      undefined,
      userHasChangedTicketQuantity.value
        ? selectedTicketQuantity.value
        : undefined
    )
    return
  }

  const pricingData = selectedDatePricingData.value
  if (!pricingData) {
    return
  }

  const queryId = route?.query?.qid?.toString() || ''
  const cartItem = parseEventDateToCartItem(
    eventData,
    selectedTicketQuantity,
    pricingData,
    selectedDate as Ref<EventDate>,
    selectedAddOns,
    isPrivate.value,
    queryId
  )

  if (!cartItem || isInCart(cartItem)) {
    return
  }

  await book()
}

watch(
  canAutoAddFromCategoryQuery,
  (canAdd) => {
    if (canAdd) {
      void tryAutoAddFromCategoryQuery()
    }
  },
  { immediate: true }
)
</script>

<style lang="postcss" scoped>
.buy-box-card {
  @apply text-stone-700 rounded-2xl *:px-2.5 pb-2.5;
  box-shadow: 0px 2px 12px 0px #0000000f;
}
</style>
